// frontend/app/components/discoveryJourneys/journeyMastery.js
//
// Reading a mastery-map payload. Pure functions, no rendering — so the one rule
// that matters most is testable on its own: a missing score is `null`, and
// `null` is never 0.

/**
 * Band keys in ascending order. Mirrors BAND_KEYS in
 * backend/services/mastery_bands.py so a segment here means the same thing as
 * the row it came from.
 */
export const BAND_ORDER = ["discovering", "building", "applying", "extending"];

/**
 * Boundaries mirror MASTERY_BANDS in backend/services/mastery_bands.py —
 * `m < 0.2` Discovering, `< 0.6` Building, `< 0.9` Applying, else Extending.
 * Only reached when the payload did not name a band; the server's own `band`
 * always wins, so there is one authority and this is the fallback.
 */
export const bandKeyFor = (mastery) => {
  if (mastery === null || mastery === undefined) return null;
  if (mastery < 0.2) return "discovering";
  if (mastery < 0.6) return "building";
  if (mastery < 0.9) return "applying";
  return "extending";
};

/**
 * The score to draw, as a 0..1 number — or null.
 *
 * Null is the whole point of this function. A concept nobody has been checked
 * on is *unknown*; rendering it as 0 would be a lie a teacher acts on. So
 * anything that is not a finite number comes back as null, and a real 0 comes
 * back as 0.
 */
export const masteryOf = (concept) => {
  const raw = concept?.mastery ?? concept?.mean_mastery ?? concept?.mastery_mean;
  if (raw === null || raw === undefined || raw === "") return null;
  const value = Number(raw);
  if (!Number.isFinite(value)) return null;
  return Math.min(1, Math.max(0, value));
};

/**
 * The sample behind the bar: how many students in teacher mode, how many pieces
 * of evidence in student mode. Different nouns, same job — say how much this
 * number is worth.
 */
export const sampleOf = (concept, mode) => {
  const raw =
    mode === "teacher"
      ? concept?.student_count ?? concept?.n ?? concept?.evidence_count
      : concept?.evidence_count ?? concept?.n ?? concept?.student_count;
  const value = Number(raw);
  return Number.isFinite(value) && value > 0 ? value : 0;
};

/** The four band counts in `BAND_ORDER`, and their total. */
export const distributionOf = (concept) => {
  const raw = concept?.distribution || {};
  const counts = BAND_ORDER.map((key) => {
    const value = Number(raw[key]);
    return Number.isFinite(value) && value > 0 ? value : 0;
  });
  return { counts, total: counts.reduce((sum, value) => sum + value, 0) };
};

/** Rows worth drawing: a bar with no concept to name is not a bar. */
export const usableConcepts = (concepts) =>
  (Array.isArray(concepts) ? concepts : []).filter(
    (concept) => concept && concept.concept_key
  );
